import {
  Component,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import {
  PayPalMessages,
  PayPalScriptProvider,
  ReactPayPalScriptOptions,
} from '@paypal/react-paypal-js';
import type { PayPalMessagesComponentOptions } from '@paypal/paypal-js';
import { REEBELO_CURRENCY } from '@lambda/reebelo';
import settings from '@/settings';
import { logger } from '@/lib/logger';
import {
  getPayPalLocale,
  getPayPalMessagesLanguage,
} from '@/lib/paypal-locale';

// Per-store client id (`chamber exec ${STORE}` at build time), so PayPal picks
// the Pay Later product, instalments and price range of that store's merchant
// country. We only pass the amount and currency.
const PAYPAL_CLIENT_ID = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID;

// `useLayoutEffect` warns when rendered on the server.
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

// The SDK's types only list USD/GBP/EUR, but messaging also covers AUD and
// CAD. Stores whose currency has no Pay Later product (NZD, SEK, ...) still
// load fine: PayPal just renders nothing.
const CURRENCY = REEBELO_CURRENCY[settings.store] as NonNullable<
  PayPalMessagesComponentOptions['currency']
>;

// Hard-coded until we decide whether PAYPAL_CLIENT_ID can be removed in the unsupported stores (NZ/SG).
const PAY_LATER_STORES: string[] = [
  'reebelo-us',
  'reebelo-ca',
  'reebelo-uk',
  'reebelo-au',
  'reebelo-fr',
];
const CAN_SHOW_MESSAGE =
  Boolean(PAYPAL_CLIENT_ID) && PAY_LATER_STORES.includes(settings.store);

// The UK and FR messages are long enough to wrap on every screen size.
const TWO_LINE_STORES: string[] = ['reebelo-uk', 'reebelo-fr'];

// One line of the message, two on the narrowest phones.
const RESERVED_HEIGHT = TWO_LINE_STORES.includes(settings.store)
  ? 'min-h-[32px]'
  : 'min-h-[32px] xxs:min-h-[20px]';

/**
 * Loads the PayPal JS SDK `messages` component once for everything under it.
 * Without a client id it renders the children as-is and every
 * `<PayLaterMessage />` below renders nothing.
 */
export const PayLaterProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();

  if (!PAYPAL_CLIENT_ID) {
    return <>{children}</>;
  }

  const options: ReactPayPalScriptOptions = {
    clientId: PAYPAL_CLIENT_ID,
    currency: CURRENCY,
    components: 'messages',
  };
  const locale = getPayPalLocale(settings.store, router.locale);

  if (locale) {
    options.locale = locale as ReactPayPalScriptOptions['locale'];
  }

  return (
    <PayPalScriptProvider options={options}>{children}</PayPalScriptProvider>
  );
};

class PayLaterBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  override state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  // eslint-disable-next-line class-methods-use-this
  override componentDidCatch(err: unknown) {
    logger.warn({ err }, 'PayPal Pay Later message failed to render');
  }

  override render() {
    return this.state.hasError ? null : this.props.children;
  }
}

type PayLaterMessageProps = {
  /** Displayed device price, in major units (e.g. 586.95). */
  amount: number;
  align?: 'left' | 'center' | 'right';
  className?: string;
};

/**
 * One PayPal message for one amount. Reports whether PayPal actually rendered
 * something.
 *
 * "Nothing" still leaves a zero-height iframe in the container, which PayPal
 * also gives an inline `min-height` placeholder it never removes, so it can't
 * be told by `:empty` or the container's own height: only by one of PayPal's
 * iframes having a height.
 */
const Slot = ({
  amount,
  align,
  hidden,
  onRendered,
}: {
  amount: number;
  align: NonNullable<PayLaterMessageProps['align']>;
  hidden: boolean;
  onRendered: (hasMessage: boolean) => void;
}) => {
  const router = useRouter();
  const ref = useRef<HTMLDivElement>(null);
  const onRenderedRef = useRef(onRendered);

  // After commit, never during render: a render React abandons must not
  // leave its callback here. A layout effect, so it's current before any
  // observer below can fire.
  useIsomorphicLayoutEffect(() => {
    onRenderedRef.current = onRendered;
  });

  useEffect(() => {
    const el = ref.current;

    if (
      !el ||
      typeof ResizeObserver === 'undefined' ||
      typeof MutationObserver === 'undefined'
    ) {
      return undefined;
    }

    const check = () =>
      onRenderedRef.current(
        Array.from(el.querySelectorAll('iframe')).some(
          (frame) => frame.offsetHeight > 0,
        ),
      );

    // PayPal adds its iframes after render and resizes them once the message
    // is known, so watch for both.
    const resizeObserver = new ResizeObserver(check);
    const mutationObserver = new MutationObserver(() => {
      el.querySelectorAll('iframe').forEach((frame) =>
        resizeObserver.observe(frame),
      );
      check();
    });

    mutationObserver.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden={hidden || undefined}
      // Loading behind the one on screen: laid out (so PayPal can size it)
      // but neither seen nor taking space.
      className={classNames(
        hidden && 'pointer-events-none invisible absolute inset-x-0 top-0',
      )}
    >
      <PayPalMessages
        className="!min-h-0"
        // Not in the SDK's types, but read by PayPal.
        {...{
          language: getPayPalMessagesLanguage(settings.store, router.locale),
        }}
        amount={amount}
        currency={CURRENCY}
        placement="product"
        style={{
          layout: 'text',
          logo: { type: 'inline' },
          text: { size: 12, color: 'black', align },
        }}
      />
    </div>
  );
};

// How long a new amount's message may take before the old one gives way
// anyway: PayPal renders nothing for an amount outside its range.
const SWAP_TIMEOUT_MS = 4000;

/**
 * The PayPal SDK ignores prop changes, so each amount needs its own message.
 * Swapping straight to it would empty the slot until PayPal fills the new
 * one, and the page jumps twice. Instead the new message loads hidden behind
 * the current one, which stays until the new one has rendered.
 *
 * The slot's spacing (`className`) and one line of height are always reserved.
 */
const Message = ({
  amount,
  align,
  className,
}: Required<Omit<PayLaterMessageProps, 'className'>> & {
  className?: string;
}) => {
  // A price of 0 is a variant still loading: keep what's shown.
  const [shown, setShown] = useState(amount > 0 ? amount : null);
  const [next, setNext] = useState<number | null>(null);
  const [hasMessage, setHasMessage] = useState(false);

  useEffect(() => {
    if (!(amount > 0) || amount === shown) {
      setNext(null);

      return;
    }

    if (shown === null || !hasMessage) {
      // Nothing on screen to hold the space: show the new one straight away.
      setShown(amount);
      setNext(null);

      return;
    }

    setNext(amount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount]);

  useEffect(() => {
    if (next === null) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setShown(next);
      setNext(null);
      setHasMessage(false);
    }, SWAP_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [next]);

  return (
    <div
      className={classNames(
        // Capped so the text wraps rather than spanning the whole buybox,
        // kept on the side it's aligned to.
        'relative w-full max-w-[400px]',
        RESERVED_HEIGHT,
        align === 'center' && 'mx-auto',
        align === 'right' && 'ml-auto',
        className,
      )}
    >

      {[shown, next].map(
        (value) =>
          value !== null && (
            <Slot
              key={`${value}-${align}`}
              amount={value}
              align={align}
              hidden={value !== shown}
              onRendered={(rendered) => {
                if (value === shown) {
                  setHasMessage(rendered);
                } else if (value === next && value === amount && rendered) {
                  // `value === amount`: until the effect above catches up, a
                  // newer amount can arrive with `next` still the older one,
                  // which must not be promoted on its way out.
                  setShown(value);
                  setNext(null);
                  setHasMessage(true);
                }
              }}
            />
          ),
      )}
    </div>
  );
};

const PayLaterMessage = ({
  amount,
  align = 'center',
  className,
}: PayLaterMessageProps) => {
  if (!CAN_SHOW_MESSAGE) {
    return null;
  }

  return (
    <PayLaterBoundary>
      <Message amount={amount} align={align} className={className} />
    </PayLaterBoundary>
  );
};

export default PayLaterMessage;
